import { ReactNode } from 'react';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { MdEdit } from 'react-icons/md';
import View from '../atoms/view';
import { Card } from '../atoms/card';
import { IconBadge } from '../atoms/iconBadge';
import { P } from '../atoms/text';
import { H3, Label, StatusValue, SubTitle } from '../atoms/text/text';
import { Colors, Spacing } from '../../constants/theme';
import theme from '../../utils/theme';

const IconEdit = MdEdit as React.ComponentType<React.SVGProps<SVGSVGElement>>;

export type VehicleInformationDetail = {
  label?: string;
  value?: ReactNode;
};

type VehicleInformationCardProps = {
  title?: string;
  icon?: ReactNode;
  headline?: ReactNode;
  details?: VehicleInformationDetail[];
  emptyMessage?: string;
  onEdit?: () => void;
  onVerify?: () => void;
  needsVerification?: boolean;
};

export const VehicleInformationCard = ({
  title = 'Vehicle information',
  icon,
  headline,
  details = [],
  emptyMessage,
  onEdit,
  onVerify,
  needsVerification,
}: VehicleInformationCardProps) => {
  const visibleDetails = details.filter((detail) => {
    if (detail.value === undefined || detail.value === null || detail.value === '') {
      return false;
    }
    return true;
  });

  return (
    <Card>
      <View extend={styles.header}>
        <View extend={styles.headerTitle}>
          <IconBadge>
            {icon ?? <DirectionsCarIcon sx={{ fontSize: 20, color: Colors.light.green }} />}
          </IconBadge>
          <SubTitle noPadding>
            {title}
          </SubTitle>
        </View>
        {needsVerification ? (
          <View extend={styles.headerActions}>
            {needsVerification ? (
              <View onClick={onVerify} extend={styles.needsVerification}>
                Needs Verification
              </View>
            ) : null}
            {onEdit ? (
              <IconEdit
                aria-label="Edit"
                onClick={onEdit}
                style={styles.editIcon}
              />
            ) : null}
          </View>
        ) : null}
      </View>

      {headline ? <StatusValue>{headline}</StatusValue> : null}

      {visibleDetails.length > 0 ? (
        <View extend={styles.details}>
          {visibleDetails.map((detail, index) => (
            <Label key={detail.label ?? index} noPadding extend={styles.detailRow}>
              {detail.label ? (
                <>
                  <Label noPadding>
                    {detail.label}:
                  </Label>
                </>
              ) : null}
              {typeof detail.value === 'string' || typeof detail.value === 'number' ? (
                <H3 noPadding>
                  {detail.value}
                </H3>
              ) : (
                detail.value
              )}
            </Label>
          ))}
        </View>
      ) : emptyMessage ? (
        <P noPadding extend={styles.emptyMessage}>
          {emptyMessage}
        </P>
      ) : null}
    </Card>
  );
};

const styles = {
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: `${6 * Spacing.gap}px`,
  },
  headerTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${6 * Spacing.gap}px`,
  },
  headerActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: `${6 * Spacing.gap}px`,
    flexShrink: 0,
  },
  needsVerification: {
    color: Colors.light.warningText,
    fontSize: 14,
    fontWeight: 400,
    fontFamily: 'Futura',
    lineHeight: 1.2,
    margin: 0,
    cursor: 'pointer',
  },
  editIcon: {
    color: theme.colors.grey,
    fontSize: 18,
    cursor: 'pointer',
    display: 'block',
    flexShrink: 0,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${4 * Spacing.gap}px`,
  },
  detailRow: {
    fontSize: '14px',
    margin: 0,
    lineHeight: 1.4,
  },
  emptyMessage: {
    fontSize: '14px',
    color: Colors.light.placeholder,
    margin: 0,
  },
};
